import {
  Alert,
  Button,
  Loader,
  Pagination,
  Select,
  TextInput,
  Textarea,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconFileText,
  IconPlus,
  IconSearch,
} from "@tabler/icons-react";
import {
  type RfqServerStatus,
  type RfqSummary,
  createRfq,
  listRfqSummaries,
} from "@/lib/rfq/api";
import { useEffect, useMemo, useState } from "react";

import { Modal } from "antd";
import RfqStatusBadge from "@/components/rfq/RfqStatusBadge";
import { useNavigate } from "react-router-dom";
import { useRfqLabels } from "@/lib/rfq/labels";

const PAGE_SIZE = 20;

function todayInput(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

const STATUS_HEADING: Record<
  RfqServerStatus,
  { key: string; fallback: string }
> = {
  inquiring: { key: "hrMain.draft", fallback: "草稿" },
  underReview: { key: "hrMain.underReviewStatus", fallback: "審核中" },
  completed: { key: "hrMain.completed", fallback: "已完成" },
};

interface RfqListPageProps {
  /** 生命週期：草稿 (inquiring) → 審核中 (underReview) → 已完成 (completed) */
  status: RfqServerStatus;
}

function RfqListPage({ status }: RfqListPageProps) {
  const { t } = useRfqLabels();
  const navigate = useNavigate();
  const [rows, setRows] = useState<RfqSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    name: "",
    issueDate: todayInput(),
    deliveryLocation: "",
    paymentTerm: "",
    priceBasis: "exw",
    remark: "",
  });

  useEffect(() => {
    let active = true;
    setRows([]);
    setLoading(true);
    setError("");
    listRfqSummaries(status)
      .then((result) => {
        if (active) setRows(result);
      })
      .catch((loadError: any) => {
        if (active)
          setError(
            loadError?.message || t("rfq.loadFailed") || "載入詢價單失敗",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [status]);

  useEffect(() => {
    setKeyword("");
    setPage(1);
  }, [status]);

  const filtered = useMemo(() => {
    const value = keyword.trim().toLowerCase();
    return rows.filter((row) => {
      if (row.serverStatus !== status) return false;
      if (!value) return true;
      return (
        row.code.toLowerCase().indexOf(value) >= 0 ||
        row.name.toLowerCase().indexOf(value) >= 0 ||
        row.applicantName.toLowerCase().indexOf(value) >= 0
      );
    });
  }, [rows, keyword, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const canCreate =
    !!draft.name.trim() &&
    !!draft.issueDate &&
    !!draft.deliveryLocation.trim() &&
    !!draft.paymentTerm.trim() &&
    !!draft.priceBasis;

  const submitCreate = async () => {
    setSaving(true);
    setError("");
    try {
      const created = await createRfq({
        name: draft.name.trim(),
        issueDate: draft.issueDate,
        deliveryLocation: draft.deliveryLocation.trim(),
        paymentTerm: draft.paymentTerm.trim(),
        priceBasis: draft.priceBasis,
        remark: draft.remark.trim(),
      });
      setCreating(false);
      navigate(`/hrMainPage/rfq/${created.id}`);
    } catch (createError: any) {
      setError(
        createError?.message || t("rfq.createFailed") || "建立詢價單失敗",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mx-auto flex max-w-[1500px] flex-col gap-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("rfq.title") || "詢價單"}
          </div>
          <span className="rounded-md bg-gray-100 px-2 py-0.5 text-sm font-medium text-gray-600">
            {t(STATUS_HEADING[status].key) || STATUS_HEADING[status].fallback}
          </span>
          {status === "inquiring" ? (
            <Button
              className="ml-auto"
              color="blue"
              radius="md"
              leftSection={<IconPlus size={16} />}
              onClick={() => setCreating(true)}
            >
              {t("rfq.list.create") || "新增詢價單"}
            </Button>
          ) : null}
        </div>

        {error ? (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {error}
          </Alert>
        ) : null}

        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <TextInput
            className="min-w-[260px] flex-1"
            value={keyword}
            onChange={(event) => {
              setKeyword(event.currentTarget.value);
              setPage(1);
            }}
            placeholder={t("rfq.list.search") || "搜尋單號 / 標題 / 承辦"}
            leftSection={<IconSearch size={15} />}
            aria-label={t("rfq.list.search") || "搜尋單號 / 標題 / 承辦"}
          />
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader />
            </div>
          ) : paged.length === 0 ? (
            <p className="py-16 text-center text-sm text-gray-500">
              {t("rfq.list.empty") || "尚無詢價單，點「新增詢價單」開始。"}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500">
                  <th className="px-6 py-2.5 font-medium">
                    {t("rfq.list.colCode") || "詢價單號"}
                  </th>
                  <th className="px-4 py-2.5 font-medium">
                    {t("rfq.list.colName") || "案件名稱"}
                  </th>
                  <th className="px-4 py-2.5 font-medium">
                    {t("rfq.list.colIssueDate") || "發出日期"}
                  </th>
                  <th className="px-4 py-2.5 font-medium">
                    {t("rfq.list.colDeliveryLocation") || "交貨地點"}
                  </th>
                  <th className="px-4 py-2.5 font-medium">
                    {t("rfq.list.colApplicant") || "承辦採購"}
                  </th>
                  <th className="px-4 py-2.5 font-medium">
                    {t("rfq.list.colStatus") || "狀態"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paged.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-blue-50/40"
                    onClick={() => navigate(`/hrMainPage/rfq/${row.id}`)}
                  >
                    <td className="px-6 py-3 font-medium text-blue-600">
                      <span className="inline-flex items-center gap-1.5">
                        <IconFileText size={14} />
                        {row.code}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{row.name}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {row.issueDate || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {row.deliveryLocation || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {row.applicantName || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <RfqStatusBadge status={row.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 ? (
          <div className="flex justify-end">
            <Pagination
              value={current}
              onChange={setPage}
              total={totalPages}
              size="sm"
            />
          </div>
        ) : null}
      </div>

      <Modal
        open={creating}
        onCancel={() => setCreating(false)}
        width={560}
        footer={null}
        destroyOnClose
        title={t("rfq.list.create") || "新增詢價單"}
      >
        <div className="flex flex-col gap-3 pt-2">
          <TextInput
            withAsterisk
            label={t("rfq.info.title") || "詢價標題"}
            value={draft.name}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, name: event.currentTarget.value }))
            }
          />
          <TextInput
            withAsterisk
            type="date"
            label={t("rfq.info.issueDate") || "發出日期"}
            value={draft.issueDate}
            onChange={(event) =>
              setDraft((prev) => ({
                ...prev,
                issueDate: event.currentTarget.value,
              }))
            }
          />
          <TextInput
            withAsterisk
            label={t("rfq.info.deliverTo") || "交貨地點"}
            value={draft.deliveryLocation}
            onChange={(event) =>
              setDraft((prev) => ({
                ...prev,
                deliveryLocation: event.currentTarget.value,
              }))
            }
          />
          <TextInput
            withAsterisk
            label={t("rfq.info.paymentTerms") || "我方付款條件"}
            value={draft.paymentTerm}
            onChange={(event) =>
              setDraft((prev) => ({
                ...prev,
                paymentTerm: event.currentTarget.value,
              }))
            }
          />
          <Select
            withAsterisk
            label={t("rfq.info.priceBasis") || "價格基準"}
            value={draft.priceBasis}
            onChange={(value) =>
              setDraft((prev) => ({ ...prev, priceBasis: value ?? "exw" }))
            }
            data={[
              {
                value: "exw",
                label: t("rfq.info.priceBasisExw") || "出廠價 EXW",
              },
              {
                value: "dap",
                label: t("rfq.info.priceBasisDap") || "送達工地 DAP",
              },
            ]}
          />
          <Textarea
            autosize
            minRows={2}
            label={t("rfq.info.remark") || "備註"}
            value={draft.remark}
            onChange={(event) =>
              setDraft((prev) => ({
                ...prev,
                remark: event.currentTarget.value,
              }))
            }
          />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="default"
            radius="md"
            onClick={() => setCreating(false)}
          >
            {t("rfq.list.cancel") || "取消"}
          </Button>
          <Button
            radius="md"
            color="blue"
            loading={saving}
            disabled={!canCreate}
            onClick={submitCreate}
          >
            {t("rfq.list.confirmCreate") || "建立"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default RfqListPage;
